package com.schoolconnect.app.feature.itusers

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogInputSize
import com.schoolconnect.app.designsystem.AppDialogLabelSize
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.close_x
import org.jetbrains.compose.resources.painterResource

private val ITUsersDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors

@Composable
internal fun ITUsersViewModal(
    open: Boolean,
    user: ITUserRowUi,
    onClose: () -> Unit
) {
    if (!open) return

    val roleText = roleLabel(user.role)
    val (statusText, statusColor) = when (user.status) {
        ITUserStatusUi.ACTIVE -> "Active" to ITUsersSuccess
        ITUserStatusUi.INACTIVE -> "Inactive" to ITUsersDanger
    }

    AppDialogScaffold(
        onDismiss = onClose,
        size = AppDialogSize.Standard,
        colors = ITUsersDialogColors,
        dismissOnClickOutside = true,
        maxDialogHeight = 900.dp
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            ITUsersViewHeader(user = user, onClose = onClose)
            AppDialogDivider(colors = ITUsersDialogColors)
            AppDialogFormGrid(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f, fill = false),
                compactBreakpoint = 680.dp
            ) { compact ->
                val scrollState = rememberScrollState()
                val gap = 16.dp
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(scrollState)
                        .padding(horizontal = 24.dp, vertical = 24.dp),
                    verticalArrangement = Arrangement.spacedBy(gap)
                ) {
                    if (compact) {
                        ITUsersViewField(label = "Full Name", value = user.name)
                        ITUsersViewField(label = "Mobile Number", value = user.mobile)
                        ITUsersViewField(label = "Email Address", value = user.email)
                        ITUsersViewField(label = "Role", value = roleText)
                        ITUsersViewField(label = "Status", value = statusText, valueColor = statusColor)
                    } else {
                        ITUsersViewFieldRow(
                            leftLabel = "Full Name",
                            leftValue = user.name,
                            rightLabel = "Mobile Number",
                            rightValue = user.mobile
                        )
                        ITUsersViewFieldRow(
                            leftLabel = "Email Address",
                            leftValue = user.email,
                            rightLabel = "Role",
                            rightValue = roleText
                        )
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            ITUsersViewField(
                                modifier = Modifier.weight(1f),
                                label = "Status",
                                value = statusText,
                                valueColor = statusColor
                            )
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }
            }
            AppDialogFooter(
                actions = listOf(
                    AppDialogFooterAction(
                        label = "Close",
                        style = AppDialogActionStyle.Primary,
                        onClick = onClose
                    )
                ),
                colors = ITUsersDialogColors,
                contentPadding = PaddingValues(horizontal = 24.dp)
            )
        }
    }
}

@Composable
private fun ITUsersViewHeader(
    user: ITUserRowUi,
    onClose: () -> Unit
) {
    val createdOn = user.createdOn.ifBlank { "—" }
    val updatedOn = user.updatedOn.ifBlank { "—" }
    val auditText = buildAnnotatedString {
        val labelStyle = SpanStyle(fontWeight = FontWeight.SemiBold, color = AppMuted)
        val valueStyle = SpanStyle(fontWeight = FontWeight.Normal, color = AppMuted)
        withStyle(labelStyle) { append("Created") }
        withStyle(valueStyle) { append(" · $createdOn | ") }
        withStyle(labelStyle) { append("Last modified") }
        withStyle(valueStyle) { append(" · $updatedOn") }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = 24.dp, end = 20.dp, top = 16.dp, bottom = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(end = 16.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(
                text = "View IT User",
                color = AppPrimary,
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
                maxLines = 1
            )
            Text(
                text = "IT user details and access information.",
                color = AppMuted,
                fontSize = 13.sp,
                fontWeight = FontWeight.Normal,
                maxLines = 2
            )
            Text(
                text = auditText,
                fontSize = 12.sp,
                lineHeight = 18.sp
            )
        }
        Box(
            modifier = Modifier
                .size(32.dp)
                .clickable(onClick = onClose),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = painterResource(Res.drawable.close_x),
                contentDescription = "Close",
                tint = AppMuted,
                modifier = Modifier.size(20.dp)
            )
        }
    }
}

@Composable
private fun ITUsersViewFieldRow(
    leftLabel: String,
    leftValue: String,
    rightLabel: String,
    rightValue: String,
    leftValueColor: Color? = null,
    rightValueColor: Color? = null
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        ITUsersViewField(
            modifier = Modifier.weight(1f),
            label = leftLabel,
            value = leftValue,
            valueColor = leftValueColor
        )
        ITUsersViewField(
            modifier = Modifier.weight(1f),
            label = rightLabel,
            value = rightValue,
            valueColor = rightValueColor
        )
    }
}

@Composable
private fun ITUsersViewField(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    valueColor: Color? = null
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = label,
            color = AppNavy,
            fontSize = AppDialogLabelSize,
            fontWeight = FontWeight.SemiBold
        )
        Text(
            text = value.ifBlank { "—" },
            color = valueColor ?: AppNavy,
            fontSize = AppDialogInputSize,
            fontWeight = FontWeight.Normal
        )
    }
}
